<script setup lang="ts">
import { useGlobalMessageStore } from '../../stores/globalMessageStore';
import { useCommissioningDeviceStore } from '../../stores/commissioningDeviceStore';
import { GlobalMessageTopic } from '../../models/GlobalMessage';
import { computed, h, ref } from 'vue';
import ButtonPrimary from '@shared/components/button/ButtonPrimary.vue';
import WarningIcon from '@/shared/assets/icon/warning.svg';
import CloseIcon from '@shared/assets/icon/xmark.svg';
import { message } from 'ant-design-vue';

const commissioningDeviceStore = useCommissioningDeviceStore();
const globalMessageStore = useGlobalMessageStore();

const forceClose = ref(false);
const miswiredMessage = computed(
  () => globalMessageStore.currentGlobalMessages[GlobalMessageTopic.MISWIRED],
);
const hasMiswiredError = computed(
  () => !!(miswiredMessage.value && Object.keys(miswiredMessage.value).length > 0),
);
const isModalVisible = computed(() => !forceClose.value && hasMiswiredError.value);
const messageToDisplay = computed(() => miswiredMessage.value?.msg ?? '');

const handleClose = async () => {
  forceClose.value = true;
  try {
    await commissioningDeviceStore.fetchTerminalsState();
  } catch (error) {
    message.error('Failed to detect connections. Please try again.');
  } finally {
    forceClose.value = false;
  }
};

const closeIconVNode = h(CloseIcon, { class: 'close-icon' });
</script>

<template>
  <a-modal
    :class="['com-modal', 'phase-miswire-modal']"
    v-model:visible="isModalVisible"
    :title="null"
    :footer="null"
    :mask-closable="true"
    :closable="true"
    :close-icon="closeIconVNode"
    @cancel="handleClose"
  >
    <div class="com-modal__content" data-tn="global-message-phase-miswire">
      <div class="icon-container">
        <WarningIcon />
      </div>

      <div class="text-container">
        <h1 class="title">Phase Miswiring</h1>
        <h3 class="subtitle">{{ messageToDisplay }}</h3>
        <p class="description">This is a critical issue and must be fixed before proceeding.</p>
      </div>

      <div class="button-container">
        Fixed the connections?
        <ButtonPrimary
          @click="handleClose"
          block
          class="com-modal__button"
          data-tn="global-message-popup-close-btn"
        >
          Let's try again.
        </ButtonPrimary>
      </div>
    </div>
  </a-modal>
</template>

<style scoped lang="less">
@import '../../../../assets/style/variables.less';

.com-modal__content {
  text-align: center;

  .icon-container {
    margin-bottom: 32px;
    color: @color-warning;
  }

  .text-container {
    margin-bottom: 32px;
    padding: 0 @content-side-margin;

    .title {
      font-weight: @font-weight-bold !important;
    }
    .subtitle {
      font-size: 18px;
      font-weight: @font-weight-medium !important;
    }
    .description {
      margin: 32px 0;
      padding: 0 16px;
      color: #000000;
    }
  }

  .button-container {
    color: black;
    margin-top: 6rem;
    .com-modal__button {
      width: 75%;
    }
  }
}

// Tablets and below
@media screen and (max-width: @mobile-width) {
  .com-modal__content {
    .icon-container {
      margin-bottom: 24px;
    }

    .text-container {
      margin-bottom: 24px;
      padding: 0 12px;

      .title {
        font-size: 24px;
        font-weight: @font-weight-bold !important;
      }

      .subtitle {
        font-size: 18px;
        font-weight: @font-weight-medium !important;
      }

      .description {
        margin: 24px 0;
        padding: 0 12px;
        font-size: 14px;
      }
    }

    .button-container {
      .com-modal__button {
        width: 85%;
      }
    }
  }
}

// Mobile and below
@media screen and (max-width: 600px) {
  .com-modal__content {
    .icon-container {
      margin-bottom: 20px;
    }

    .text-container {
      margin-bottom: 20px;
      padding: 0 8px;

      .title {
        font-size: 20px;
        font-weight: @font-weight-bold !important;
      }

      .subtitle {
        font-size: 16px;
        font-weight: @font-weight-medium !important;
      }

      .description {
        margin: 20px 0;
        padding: 0 8px;
        font-size: 13px;
      }
    }

    .button-container {
      .com-modal__button {
        width: 90%;
      }
    }
  }
}
</style>
